/**
 * Plan tablosunun saf yardımcıları — React'e bağımlı değil.
 *
 * Plan KONUMSALDIR: sıra N, N. periyoda denk gelir. Satır taşındığında
 * sipariş başka bir zaman dilimine geçer, zaman dilimi yerinde kalır. Bu
 * yüzden tarih/periyot satırın üstünde taşınmaz; taban plandan alınan
 * `zamanlar` dizisinden konuma göre okunur.
 */

let sayac = 0;
/** Satırın React anahtarı — taşıma/silme sonrası da kimliğini korur. */
const anahtar = () => `s${Date.now().toString(36)}-${(sayac += 1)}`;

/**
 * Duruş satırı: gövde ve gövde ön yok. Canlı plan duruşu `0` ile, taslaklar
 * ve istek sözleşmesi `null` ile gösteriyor — ikisi de duruş sayılır.
 */
const bos = (v) => v == null || Number(v) === 0;

/** API ya da taslak satırı → düzenlenebilir satır. */
export function satirdan(r) {
  const durus = bos(r.govde) && bos(r.govde_on);
  return {
    key: anahtar(),
    durus,
    govde: durus ? null : Number(r.govde) || null,
    govde_on: durus ? null : Number(r.govde_on) || null,
    uretim: durus ? 0 : Number(r.uretim) || 0,
  };
}

/** Taban plandaki zaman dilimleri, sıraya göre. */
export function zamanlarDan(satirlar) {
  return [...satirlar]
    .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
    .map((r) => ({ tarih: r.date, periyot: String(r.period ?? "").slice(0, 5) }));
}

/** Yeni satır. Duruş ise malzemesiz ve 0 adet. */
export function yeniSatir({ durus = false, govde = null, govde_on = null, uretim = 0 } = {}) {
  return {
    key: anahtar(),
    durus,
    govde: durus ? null : govde,
    govde_on: durus ? null : govde_on,
    uretim: durus ? 0 : Number(uretim) || 0,
  };
}

/** Satırı bir konum yukarı (-1) ya da aşağı (+1) taşır. */
export function tasi(satirlar, i, yon) {
  const j = i + yon;
  if (j < 0 || j >= satirlar.length) return satirlar;
  const yeni = [...satirlar];
  [yeni[i], yeni[j]] = [yeni[j], yeni[i]];
  return yeni;
}

/** Satırı `hedef` konumuna taşır (sürükle-bırak için). */
export function konumaTasi(satirlar, kaynak, hedef) {
  if (kaynak === hedef) return satirlar;
  const yeni = [...satirlar];
  const [s] = yeni.splice(kaynak, 1);
  yeni.splice(hedef, 0, s);
  return yeni;
}

/**
 * İsteklere giden plan. Sıra her seferinde KONUMDAN yeniden üretilir —
 * taşıma/silme/ekleme sonrası 1'den başlayarak kesintisiz numaralanır.
 * Duruş satırı sözleşmedeki gibi `govde: null, govde_on: null, uretim: 0`.
 */
export function planGovdesi(satirlar) {
  return satirlar.map((s, i) => ({
    sequence: i + 1,
    govde: s.durus ? null : s.govde,
    govde_on: s.durus ? null : s.govde_on,
    uretim: s.durus ? 0 : s.uretim,
  }));
}

/**
 * İki planın içeriği aynı mı — kaydedilmemiş değişiklik uyarısı için.
 * Anahtarlar değil, gönderilecek içerik karşılaştırılır.
 */
export function ayniPlan(a, b) {
  return JSON.stringify(planGovdesi(a)) === JSON.stringify(planGovdesi(b));
}

/**
 * Satırın eksik alanı var mı. Duruş dışındaki satırda iki malzeme de seçili
 * ve adet pozitif olmalı; eksik satır modele gitmeden yakalanır.
 */
export function eksikSatirlar(satirlar) {
  const eksik = [];
  satirlar.forEach((s, i) => {
    if (s.durus) return;
    if (!s.govde || !s.govde_on || !(s.uretim > 0)) eksik.push(i + 1);
  });
  return eksik;
}

/* ------------------------------------------------------------------ */
/* Asma kilidi                                                         */
/* ------------------------------------------------------------------ */

/**
 * Hattın gerçek durumu, canlı plan satırlarının bayraklarından.
 *
 * Sınır SAATLE DEĞİL BAYRAKLA belirlenir: arıza/duruşta periyotlar yeniden
 * zamanlanıyor, duvar saati yanıltır.
 *
 * Uçtaki bayraklar: şu an asmadaki satırda `is_started_asma = true`; asmayı
 * BİTİREN satırda bu bayrak `false`'a dönüyor, `is_completed_asma = true`
 * oluyor. Yalnız başlamış bayrağına bakılsaydı iki periyot arasındaki anda
 * hiçbir satır başlamış görünmez, bitmiş satırların kilidi açılırdı. Bu
 * yüzden sınır "başlamış VEYA bitmiş son satır".
 *
 * @returns {{ kilit: number, asmada: number, batchId: number|null }}
 *   kilit   baştan kaç konum kilitli (0 = hiçbiri)
 *   asmada  şu an asmadaki satırın konumu (-1 = yok)
 */
export function hatDurumu(hamSatirlar) {
  const s = [...(hamSatirlar ?? [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  let kilit = 0;
  let asmada = -1;
  s.forEach((r, i) => {
    if (r.is_started_asma || r.is_completed_asma) kilit = i + 1;
    if (r.is_started_asma && !r.is_completed_asma) asmada = i;
  });
  return { kilit, asmada, batchId: Number(s[0]?.order_batch?.id) || null };
}

/** İki satırın modele giden içeriği aynı mı (anahtar hariç). */
const ayniSatir = (a, b) => JSON.stringify(planGovdesi([a])) === JSON.stringify(planGovdesi([b]));

/**
 * Kilitli önek canlı planla BİREBİR aynı olmalı: o periyotlar asmaya girdi,
 * hat onları canlı plandaki hâliyle üretiyor. Tablodaki ilk `kilit` konumdan
 * canlıdakinden farklı olanlar canlı hâline döndürülür.
 *
 * Konuma göre çalışır (plan konumsal). Satır anahtarı korunur ki React/dnd
 * satırı baştan kurmasın.
 *
 * @returns {{ satirlar: Array, degisen: number[] }} degisen = geri alınan konumlar
 */
export function kilitliOnekiHizala(satirlar, canliSatirlar, kilit) {
  const degisen = [];
  const yeni = satirlar.map((s, i) => {
    if (i >= kilit || !canliSatirlar[i] || ayniSatir(s, canliSatirlar[i])) return s;
    degisen.push(i);
    return { ...canliSatirlar[i], key: s.key };
  });
  // Tablo kilitli önekten kısaysa (sondaki satırlar silinmişse) eksik
  // periyotlar canlıdan tamamlanır — asmaya girmiş periyot plandan düşemez.
  for (let i = yeni.length; i < Math.min(kilit, canliSatirlar.length); i += 1) {
    degisen.push(i);
    yeni.push({ ...canliSatirlar[i], key: anahtar() });
  }
  return { satirlar: degisen.length ? yeni : satirlar, degisen };
}

/** Malzeme listesinden kod → { ad, renk } sözlüğü. */
export function malzemeSozlugu(liste, renkAlani) {
  const m = new Map();
  for (const x of liste ?? []) {
    m.set(Number(x.malzeme_kodu), {
      ad: x.tanim,
      renk: x[renkAlani]?.description ?? null,
    });
  }
  return m;
}
